import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_PRODUCT_ALERTS } from "@lib/shared_constants";

import { productAlertTargetLimitReachedErrorCode } from "./constants";

export type ProductAlertStatus = "active" | "disabled";

export interface ProductAlertsParams {
  query?: {
    name?: string;
    status?: ProductAlertStatus;
    canvas_ids?: string[];
  };
  start?: number;
  limit?: number;
  sortby?: string;
  sortdir?: number;
}

export const productAlertThresholdUnitVolume = "volume" as const;
export const productAlertThresholdUnitPercentage = "percentage" as const;

export type ProductAlertThresholdUnit =
  | typeof productAlertThresholdUnitVolume
  | typeof productAlertThresholdUnitPercentage;

export interface ProductAlertCondition {
  metric: string;
  subject: string;
  operator: string;
  threshold_value: number;
  threshold_unit: ProductAlertThresholdUnit;
}

export interface ProductAlertConditionGroup {
  condition_operator: string;
  conditions: ProductAlertCondition[];
}

export interface ProductAlertRules {
  group_operator: string;
  condition_groups: ProductAlertConditionGroup[];
}

export interface ProductAlertNotificationSettings {
  email?: { recipients: string[] };
  webhook?: { url: string; headers?: Record<string, string> };
}

export interface ProductAlertSchedule {
  schedule_type: string;
  schedule_time_seconds: number;
  lookback_seconds: number;
  schedule_anchor_seconds?: number;
}

export interface UpdateProductAlertParams {
  name: string;
  target: string;
  target_id: string;
  status: ProductAlertStatus;
  rules: ProductAlertRules;
  schedule: ProductAlertSchedule;
  notification_settings: ProductAlertNotificationSettings;
}

export type CreateProductAlertParams = UpdateProductAlertParams;

export interface PostProductAlertProps extends UpdateProductAlertParams {}

export interface PutProductAlertProps extends UpdateProductAlertParams {
  id: string;
}

export interface ProductAlertListResultItem {
  id: string;
  target: string;
  target_id: string;
  target_name: string;
  name: string;
  status: ProductAlertStatus;
  rules: ProductAlertRules;
  schedule: ProductAlertSchedule;
  notification_settings?: ProductAlertNotificationSettings;
  notification_channels: string[];
  updated_at: number;
}

export interface ProductAlertResultItem extends ProductAlertListResultItem {
  notification_settings: ProductAlertNotificationSettings;
}

export interface ProductAlertsResult {
  hits: number;
  results: ProductAlertListResultItem[];
}

export interface PatchProductAlertProps {
  id: string;
  status: ProductAlertStatus;
}

export interface DeleteProductAlertProps {
  id: string;
}

/** Meta the backend attaches to the target-limit error: the cap it enforced. */
export interface ProductAlertTargetLimitReachedMeta {
  max_alerts: number;
}

export type ProductAlertTargetLimitReachedError = FetchUserEncodedError<
  SHARED_CONSTANTS_PRODUCT_ALERTS["ERROR_CODES"]["TARGET_ALERT_LIMIT_REACHED"],
  ProductAlertTargetLimitReachedMeta
>;

/**
 * Whether a failed product alert write was rejected because the target already carries its
 * maximum number of alerts (for example another user created one after this page loaded).
 */
export const isProductAlertTargetLimitReachedError = (
  error: unknown
): error is ProductAlertTargetLimitReachedError => {
  return error instanceof FetchUserEncodedError && error.errorCode === productAlertTargetLimitReachedErrorCode;
};
